import { ArrowUpRight, LayoutGrid, LogOut, Star } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { AppIcon } from './AppIcon';
import { initials, type DesktopItem } from './items';

export interface ExplorerProps {
  /** Registered apps the user can open (external URLs) — shown under "Favourites". */
  apps: DesktopItem[];
  /** OS pages (client-side routes) — shown under "System". */
  system: DesktopItem[];
  userName?: string;
  orgName?: string;
  onSignOut: () => void;
}

/**
 * Windows Explorer-style navigation pane shown at the left of every OS window: Launcher on top, the dock's
 * apps as "Favourites", then every OS page under "System", and the signed-in user in the footer.
 * Purely presentational — the caller supplies items (the same ones the dock and Start menu use).
 */
export function Explorer({ apps, system, userName, orgName, onSignOut }: ExplorerProps) {
  const pages = system.filter((s) => s.to !== '/');
  return (
    <nav className="xp" aria-label="Explorer">
      <NavLink to="/" end className="xp-item"><LayoutGrid size={16} strokeWidth={1.6} /><span>Launcher</span></NavLink>

      <div className="xp-h"><Star size={11} strokeWidth={2.2} aria-hidden />Favourites</div>
      {apps.length === 0 && <div className="xp-empty">No apps yet</div>}
      {apps.map((a) => (
        <a key={a.id} className="xp-item" href={a.href} title={a.description}>
          <AppIcon item={a} size={20} />
          <span className="truncate">{a.name}</span>
          <ArrowUpRight size={13} strokeWidth={1.8} className="xp-ext" aria-hidden />
        </a>
      ))}

      <div className="xp-h">System</div>
      {pages.map((s) => {
        const Icon = s.icon;
        return (
          <NavLink key={s.id} to={s.to!} className="xp-item" title={s.description}>
            {Icon && <Icon size={16} strokeWidth={1.6} />}
            <span className="truncate">{s.name}</span>
          </NavLink>
        );
      })}

      <div className="xp-foot">
        <span className="dk-avatar" aria-hidden>{initials(userName ?? '?')}</span>
        <span className="xp-foot-name"><b className="truncate">{userName ?? '…'}</b>{orgName && <small className="truncate">{orgName}</small>}</span>
        <button type="button" className="xp-signout" title="Sign out" aria-label="Sign out" onClick={onSignOut}><LogOut size={15} strokeWidth={1.7} /></button>
      </div>
    </nav>
  );
}
